import { useState } from "react";
import { createPortal } from "react-dom";
import { apiPatch, apiPost, type Incident } from "../api";
import { useGetIncidentQuery } from "../store/incidents";
import { useServiceSlugMap } from "../store/services";
import { Icons } from "./icons";

interface IncidentUpdate {
  id: string;
  kind: string;
  state_after?: string;
  message_public: string;
  inserted_at: string;
}

interface IncidentWithUpdates extends Incident {
  updates: IncidentUpdate[];
}

interface Props {
  incidentId: string;
  onClose: () => void;
}

const STATE_META: Record<
  string,
  { label: string; color: string; icon: keyof typeof Icons }
> = {
  investigating: { label: "Investigando", color: "var(--st-major)",    icon: "AlertTriangle" },
  identified:    { label: "Identificado", color: "var(--st-degraded)", icon: "Flag" },
  monitoring:    { label: "En monitoreo", color: "var(--st-maint)",    icon: "Activity" },
  resolved:      { label: "Resuelto",     color: "var(--st-ok)",       icon: "Check" },
};

const SEVERITY_META: Record<string, { label: string; color: string }> = {
  informational: { label: "Informativo", color: "var(--fg-3)" },
  minor:         { label: "Menor",       color: "var(--st-degraded)" },
  major:         { label: "Mayor",       color: "var(--st-major)" },
  critical:      { label: "Crítico",     color: "var(--st-major)" },
};

/**
 * Modal de detalle/edición de incidente. Reemplaza la página `/incidents/:id`
 * porque para incidentes (típicamente pocos updates, decisiones rápidas) un
 * modal mantiene al operador en la lista y agiliza el ida-y-vuelta entre
 * varios incidentes.
 *
 * Funcionalidades:
 *  - Ver toda la info del incidente
 *  - Agregar update (mensaje + opcionalmente cambiar estado)
 *  - Transicionar estado (identified, monitoring)
 *  - Resolver
 *  - Ver el timeline de updates con visual de "línea de tiempo"
 */
export function IncidentDetailModal({ incidentId, onClose }: Props) {
  const { data: inc, isLoading, refetch } = useGetIncidentQuery(incidentId) as {
    data: IncidentWithUpdates | undefined;
    isLoading: boolean;
    refetch: () => void;
  };

  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function transition(newState: string) {
    if (!message.trim()) {
      setError("Necesitás un mensaje público para registrar la transición.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await apiPatch(`/v1/incidents/${incidentId}/state`, {
        state: newState,
        message_public: message,
      });
      setMessage("");
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al transicionar");
    } finally {
      setSubmitting(false);
    }
  }

  async function resolve() {
    setSubmitting(true);
    setError(null);
    try {
      await apiPost(`/v1/incidents/${incidentId}/resolve`, {
        message_public: message || "Incidente resuelto.",
      });
      setMessage("");
      refetch();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Error al resolver");
    } finally {
      setSubmitting(false);
    }
  }

  // Cierre con click en backdrop solo si no estamos enviando
  function maybeClose() {
    if (!submitting) onClose();
  }

  if (isLoading || !inc) {
    return createPortal(
      <div className="modal-backdrop" onClick={maybeClose}>
        <div
          className="modal-card"
          onClick={(e) => e.stopPropagation()}
          style={{ width: 780, maxWidth: "94vw", padding: 32, textAlign: "center" }}
        >
          <div style={{ color: "var(--fg-3)", fontSize: 13 }}>Cargando incidente…</div>
        </div>
      </div>,
      document.body,
    );
  }

  const stateMeta = STATE_META[inc.state] ?? STATE_META.investigating;
  const sevMeta = SEVERITY_META[inc.severity] ?? SEVERITY_META.minor;
  const isResolved = inc.state === "resolved";
  const isAuto = inc.created_by_id === "system";
  const StateIcon = Icons[stateMeta.icon];

  return createPortal(
    <div className="modal-backdrop" onClick={maybeClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 820,
          maxWidth: "94vw",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          padding: 0,
        }}
      >
        {/* ── Header ──────────────────────────────────────────────────── */}
        <header
          style={{
            padding: "18px 22px",
            borderBottom: "1px solid var(--border-1)",
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 8,
              background: `color-mix(in oklab, ${stateMeta.color} 14%, transparent)`,
              color: stateMeta.color,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <StateIcon size={18} sw={1.7} />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <h2
                style={{
                  margin: 0,
                  fontSize: 17,
                  fontFamily: "var(--font-display)",
                  fontWeight: 700,
                  letterSpacing: "-0.01em",
                }}
              >
                {inc.title}
              </h2>
              {isAuto && (
                <span
                  title="Incidente creado automáticamente por el detector de errores reiterados"
                  style={{
                    fontSize: 10,
                    fontFamily: "var(--font-mono)",
                    padding: "2px 7px",
                    borderRadius: 4,
                    background: "color-mix(in oklab, var(--accent) 14%, transparent)",
                    color: "var(--accent)",
                    fontWeight: 600,
                  }}
                >
                  AUTO
                </span>
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontSize: 11.5,
                color: "var(--fg-3)",
                fontFamily: "var(--font-mono)",
              }}
            >
              <span>{inc.id}</span>
              <span>·</span>
              <SeverityBadge meta={sevMeta} />
              <span>·</span>
              <StateBadge meta={stateMeta} />
            </div>
          </div>
          <button
            type="button"
            className="btn btn-icon btn-ghost"
            onClick={maybeClose}
            aria-label="Cerrar"
            style={{ flexShrink: 0 }}
          >
            <Icons.Close size={14} />
          </button>
        </header>

        {/* ── Body (scrollable) ───────────────────────────────────────── */}
        <div
          style={{
            overflowY: "auto",
            padding: 22,
            display: "flex",
            flexDirection: "column",
            gap: 20,
          }}
        >
          {/* Info bullets — clean grid layout */}
          <InfoGrid inc={inc} />

          {/* Mensaje público (descripción larga si tiene) */}
          {inc.message_public && (
            <Section title="Mensaje público" icon={<Icons.Mail size={12} sw={1.8} />}>
              <div
                style={{
                  padding: "12px 14px",
                  background: "var(--bg-2)",
                  border: "1px solid var(--border-1)",
                  borderRadius: 6,
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: "var(--fg-1)",
                  whiteSpace: "pre-wrap",
                }}
              >
                {inc.message_public}
              </div>
            </Section>
          )}

          {/* Update form — solo si NO está resuelto */}
          {!isResolved && (
            <Section title="Actualizar incidente" icon={<Icons.Pencil size={12} sw={1.8} />}>
              <textarea
                className="input"
                rows={3}
                placeholder="Mensaje público (qué pasó, qué estamos haciendo, ETA)"
                value={message}
                onChange={(e) => {
                  setMessage(e.target.value);
                  if (error) setError(null);
                }}
                style={{ width: "100%", resize: "vertical", minHeight: 70 }}
              />
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginTop: 10,
                  alignItems: "center",
                }}
              >
                <span style={{ fontSize: 11.5, color: "var(--fg-3)", marginRight: 4 }}>
                  Transicionar a:
                </span>
                <TransitionButton
                  active={inc.state === "identified"}
                  disabled={submitting || inc.state === "identified"}
                  onClick={() => transition("identified")}
                  color="var(--st-degraded)"
                >
                  <Icons.Flag size={11} sw={2} />
                  Identificado
                </TransitionButton>
                <TransitionButton
                  active={inc.state === "monitoring"}
                  disabled={submitting || inc.state === "monitoring"}
                  onClick={() => transition("monitoring")}
                  color="var(--st-maint)"
                >
                  <Icons.Activity size={11} sw={2} />
                  En monitoreo
                </TransitionButton>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={resolve}
                  disabled={submitting}
                  style={{
                    marginLeft: "auto",
                    background: "var(--st-ok)",
                    color: "#fff",
                    border: "none",
                  }}
                >
                  <Icons.Check size={12} sw={2.2} />
                  <span style={{ marginLeft: 6 }}>
                    {submitting ? "Resolviendo…" : "Resolver incidente"}
                  </span>
                </button>
              </div>
              {error && (
                <div
                  style={{
                    marginTop: 10,
                    padding: "8px 12px",
                    background: "color-mix(in oklab, var(--st-major) 10%, transparent)",
                    border: "1px solid color-mix(in oklab, var(--st-major) 30%, transparent)",
                    borderRadius: 6,
                    fontSize: 12.5,
                    color: "var(--st-major)",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <Icons.AlertTriangle size={13} />
                  {error}
                </div>
              )}
            </Section>
          )}

          {/* Timeline de updates */}
          {inc.updates && inc.updates.length > 0 && (
            <Section title="Historial" icon={<Icons.Clock size={12} sw={1.8} />}>
              <Timeline updates={inc.updates} />
            </Section>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <footer
          style={{
            padding: "12px 22px",
            borderTop: "1px solid var(--border-1)",
            background: "var(--bg-1)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 11,
            color: "var(--fg-3)",
          }}
        >
          <span>
            {isResolved
              ? "Este incidente ya fue resuelto."
              : "Cualquier actualización notifica a los suscriptores activos."}
          </span>
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cerrar
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ─── Sub-componentes ─────────────────────────────────────────────────────────

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          fontWeight: 600,
          color: "var(--fg-3)",
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        {icon}
        {title}
      </div>
      {children}
    </section>
  );
}

function InfoGrid({ inc }: { inc: IncidentWithUpdates }) {
  const slugMap = useServiceSlugMap();
  const items: Array<[string, React.ReactNode]> = [
    [
      "Abierto",
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>
        {new Date(inc.opened_at).toLocaleString("es-AR", {
          dateStyle: "medium",
          timeStyle: "short",
        })}
      </span>,
    ],
  ];
  if (inc.resolved_at) {
    items.push([
      "Resuelto",
      <span style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--st-ok)" }}>
        {new Date(inc.resolved_at).toLocaleString("es-AR", {
          dateStyle: "medium",
          timeStyle: "short",
        })}
      </span>,
    ]);
  }
  items.push([
    "Servicios afectados",
    inc.affected_service_ids.length === 0 ? (
      <span style={{ color: "var(--fg-4)" }}>—</span>
    ) : (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {inc.affected_service_ids.map((s) => (
          <span key={s} className="svc-chip" title={s}>
            <span className="dot status-dot major" />
            {slugMap[s] ?? s.slice(0, 8)}
          </span>
        ))}
      </div>
    ),
  ]);

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "140px 1fr",
        rowGap: 10,
        columnGap: 18,
        padding: "12px 14px",
        background: "var(--bg-1)",
        border: "1px solid var(--border-1)",
        borderRadius: 8,
        fontSize: 13,
      }}
    >
      {items.map(([k, v]) => (
        <div key={k} style={{ display: "contents" }}>
          <div style={{ color: "var(--fg-3)", fontSize: 11.5, fontWeight: 600 }}>{k}</div>
          <div style={{ color: "var(--fg-1)" }}>{v}</div>
        </div>
      ))}
    </div>
  );
}

function Timeline({ updates }: { updates: IncidentUpdate[] }) {
  // Ordenar desc (más reciente primero)
  const sorted = [...updates].sort(
    (a, b) => new Date(b.inserted_at).getTime() - new Date(a.inserted_at).getTime(),
  );
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 14 }}>
      {sorted.map((u, idx) => {
        const sm = u.state_after ? STATE_META[u.state_after] : null;
        const dotColor = sm?.color ?? "var(--accent)";
        const isLast = idx === sorted.length - 1;
        return (
          <li key={u.id} style={{ display: "flex", gap: 12, position: "relative" }}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                flexShrink: 0,
                width: 14,
              }}
            >
              <span
                style={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: dotColor,
                  marginTop: 5,
                  boxShadow: `0 0 0 3px color-mix(in oklab, ${dotColor} 18%, transparent)`,
                }}
              />
              {!isLast && (
                <span
                  style={{
                    flex: 1,
                    width: 2,
                    background: "var(--border-1)",
                    marginTop: 4,
                    minHeight: 16,
                  }}
                />
              )}
            </div>
            <div style={{ flex: 1, paddingBottom: isLast ? 0 : 4 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  fontSize: 11.5,
                  color: "var(--fg-3)",
                  fontFamily: "var(--font-mono)",
                  marginBottom: 5,
                }}
              >
                <span>{new Date(u.inserted_at).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" })}</span>
                <span
                  style={{
                    padding: "1px 6px",
                    borderRadius: 4,
                    background: "var(--bg-2)",
                    border: "1px solid var(--border-1)",
                    color: "var(--fg-2)",
                    fontSize: 10,
                  }}
                >
                  {u.kind}
                </span>
                {u.state_after && sm && (
                  <span
                    style={{
                      padding: "1px 6px",
                      borderRadius: 4,
                      background: `color-mix(in oklab, ${sm.color} 12%, transparent)`,
                      color: sm.color,
                      fontSize: 10,
                      fontWeight: 600,
                    }}
                  >
                    → {sm.label}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: 13,
                  lineHeight: 1.5,
                  whiteSpace: "pre-wrap",
                  color: "var(--fg-1)",
                  padding: "8px 12px",
                  background: "var(--bg-2)",
                  border: "1px solid var(--border-1)",
                  borderRadius: 6,
                }}
              >
                {u.message_public}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function SeverityBadge({ meta }: { meta: { label: string; color: string } }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "1px 7px",
        borderRadius: 4,
        background: `color-mix(in oklab, ${meta.color} 14%, transparent)`,
        color: meta.color,
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
      }}
    >
      {meta.label}
    </span>
  );
}

function StateBadge({
  meta,
}: {
  meta: { label: string; color: string; icon: keyof typeof Icons };
}) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "1px 7px",
        borderRadius: 4,
        background: `color-mix(in oklab, ${meta.color} 14%, transparent)`,
        color: meta.color,
        fontFamily: "var(--font-mono)",
        fontSize: 10.5,
        fontWeight: 600,
      }}
    >
      {meta.label}
    </span>
  );
}

function TransitionButton({
  active,
  disabled,
  onClick,
  color,
  children,
}: {
  active: boolean;
  disabled: boolean;
  onClick: () => void;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 12px",
        background: active ? `color-mix(in oklab, ${color} 16%, transparent)` : "transparent",
        border: `1px solid color-mix(in oklab, ${color} 35%, var(--border-1))`,
        borderRadius: 6,
        color: disabled ? "var(--fg-3)" : color,
        fontSize: 12,
        fontWeight: 600,
        fontFamily: "var(--font-display)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "background 0.15s, transform 0.1s",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        (e.currentTarget as HTMLButtonElement).style.background =
          `color-mix(in oklab, ${color} 14%, transparent)`;
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        (e.currentTarget as HTMLButtonElement).style.background = active
          ? `color-mix(in oklab, ${color} 16%, transparent)`
          : "transparent";
      }}
    >
      {children}
    </button>
  );
}
